import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { EditIcon } from '@common/components/icons';
import * as styles from './styles.module.css';

/**
 * Header Component
 * 
 * Displays a page header with an optional subtitle and action buttons bar
 * 
 * @param {string} children - The main header title (required)
 * @param {string} subtitle - Optional subtitle text displayed below the main title
 * @param {boolean} subtitleEditable - If true, shows an edit icon next to the subtitle
 * @param {function} onSubtitleEdit - Callback when edit icon is clicked (required if subtitleEditable is true)
 * @param {string} editAriaLabel - Accessibility label for the edit button (uses i18n default if not provided)
 * @param {React.ReactNode} actions - Optional action buttons to display in a grey bar below the header
 * 
 * @example
 * // Simple header
 * <Header>Agent Management</Header>
 * 
 * @example
 * // Header with subtitle
 * <Header subtitle="History Report">Reports</Header>
 * 
 * @example
 * // Header with editable subtitle
 * <Header 
 *   subtitle="My Report" 
 *   subtitleEditable 
 *   onSubtitleEdit={() => setShowModal(true)}
 *   editAriaLabel="Edit report name"
 * >
 *   Reports
 * </Header>
 * 
 * @example
 * // Header with action buttons
 * <Header 
 *   subtitle="My Report"
 *   actions={
 *     <>
 *       <Button variant="outline">Refresh</Button>
 *       <Button variant="outline">Export</Button>
 *     </>
 *   }
 * >
 *   Reports
 * </Header>
 */
export default function Header({ 
  children, 
  subtitle, 
  subtitleEditable, 
  onSubtitleEdit,
  editAriaLabel,
  actions
}) {
  const { t } = useTranslation();

  return (
    <>
      <header className={styles['page-header']}>
        <div className={styles['header-title']}>{children}</div>
        {subtitle && (
          <div className={styles['header-subtitle-container']}>
            <span className={styles['header-subtitle']}>{subtitle}</span>
            {subtitleEditable && onSubtitleEdit && (
              <button
                type="button"
                className={styles['edit-subtitle-button']}
                onClick={onSubtitleEdit}
                aria-label={editAriaLabel || t('common.edit')}
              >
                <EditIcon className={styles['edit-icon']} />
              </button>
            )}
          </div>
        )}
      </header>
      {actions && (
        <div className={styles['header-actions-bar']}>
          <div className={styles['header-actions']}>
            {actions}
          </div>
        </div>
      )}
    </>
  );
}

Header.propTypes = {
  children: PropTypes.node.isRequired,
  subtitle: PropTypes.string,
  subtitleEditable: PropTypes.bool,
  onSubtitleEdit: PropTypes.func,
  editAriaLabel: PropTypes.string,
  actions: PropTypes.node,
};

Header.defaultProps = {
  subtitle: null,
  subtitleEditable: false,
  onSubtitleEdit: null,
  editAriaLabel: null,
  actions: null,
};
